import {
  type LineageModificationsMap,
  type Modification,
  type ModificationsMap,
  type ProductIngredients,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a list of modifications based on the provided ingredients and modifiers map.
 * It accumulates and updates modifications with respect to the available ingredient portions.
 *
 * When a lineage map is provided, ingredients whose id is not on the current
 * product fall back to the modification sharing their lineage key. This lets a
 * line item from another restaurant resolve against the local menu.
 */
export function generateModificationsList(
  ingredients: ProductIngredients,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: LineageModificationsMap,
) {
  // A lineage key can match several modifications on this product. Give each
  // distinct incoming ingredient id its own slot so portions stay spread across
  // groups the way the product offers them, instead of collapsing onto one and
  // breaching that group's aggregate limit.
  const claimedByIngredientId = new Map<string, Modification>();
  const claimedSlots = new Set<string>();

  const modificationsMap = ingredients.reduce<ModificationsMap>((map, ingredient) => {
    const modification = claimModification({
      ingredient,
      allModificationsMap,
      lineageModificationsMap,
      claimedByIngredientId,
      claimedSlots,
    }); // prettier-ignore

    // ─── Invalid modification ────────────────────────────────────────────

    if (!modification) return map;

    // The fallback is just only satisfies the nullable generated type.
    const ingredientId = modification.ingredient?.id ?? ingredient.id;

    const maybeExistingModification = map.get(ingredientId);

    // ─── Bump quantity if multiple portions are available ────────────────

    if (maybeExistingModification) {
      map.set(ingredientId, withOutOfStockFlag(withUpdatedQuantity(maybeExistingModification))); // prettier-ignore

      return map;
    }

    // ─── Initial portion ─────────────────────────────────────────────────

    map.set(ingredientId, withOutOfStockFlag(modification));

    return map;
  }, new Map());

  return {
    modificationsMap,
    modificationsList: [...modificationsMap.values()],
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the product modification for an ingredient by id, then by lineage key.
 * Falls back to the first modification sharing the key, for callers that resolve
 * a single ingredient with no notion of which slot is still free.
 */
export function resolveModification(
  ingredient: Pick<ProductIngredients[number], 'id' | 'lineageKey'>,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: LineageModificationsMap,
) {
  const modificationById = allModificationsMap.get(ingredient.id);

  if (modificationById) {
    return modificationById;
  }

  const lineageKey = ingredient.lineageKey;

  if (!lineageKey || !lineageModificationsMap) {
    return;
  }

  return lineageModificationsMap.get(lineageKey)?.[0];
}

/**
 * Resolves a modification while tracking which lineage slots are already taken,
 * so repeated portions of one ingredient reuse their slot and distinct
 * ingredients take distinct ones.
 */
function claimModification(params: ClaimModificationParams) {
  const {
    ingredient,
    allModificationsMap,
    lineageModificationsMap,
    claimedByIngredientId,
    claimedSlots,
  } = params;

  const modificationById = allModificationsMap.get(ingredient.id);

  if (modificationById) {
    return modificationById;
  }

  const lineageKey = ingredient.lineageKey;

  if (!lineageKey || !lineageModificationsMap) return;

  const alreadyClaimed = claimedByIngredientId.get(ingredient.id);

  if (alreadyClaimed) {
    return alreadyClaimed;
  }

  const candidates = lineageModificationsMap.get(lineageKey) ?? [];
  const freeCandidate = candidates.find((candidate) => !claimedSlots.has(slotKey(candidate)));
  const claimed = freeCandidate ?? candidates[0];

  if (!claimed) return;

  claimedByIngredientId.set(ingredient.id, claimed);
  claimedSlots.add(slotKey(claimed));

  return claimed;
}

function slotKey(modification: Modification) {
  return `${modification.ingredient?.id ?? modification.ingredientId}-${modification.modificationGroupId}`;
}

function withUpdatedQuantity(modification: Modification): Modification {
  return { ...modification, quantity: modification.quantity + 1 };
}

function withOutOfStockFlag(modification: Modification): Modification {
  return { ...modification, outOfStock: modification.outOfStock };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ClaimModificationParams = {
  ingredient: ProductIngredients[number];
  allModificationsMap: ModificationsMap;
  lineageModificationsMap?: LineageModificationsMap;
  claimedByIngredientId: Map<string, Modification>;
  claimedSlots: Set<string>;
};
